import {gql} from '@apollo/client';
import {Flex} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {compose} from 'redux';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentStore from 'notebook/documents/DocumentStore';
import {EditorPropertiesContext, EditorPropertiesContextInterface} from 'notebook/notes/core-ui/NoteContext';
import DocumentUpdatesManager from 'notebook/realtime/DocumentUpdatesManager';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {
  StructuredTableInsertionContext,
  StructuredTableInsertionContextValue,
} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {ActiveCrossStepFormulaBox} from 'notebook/tables/core-ui/CrossStepFormulaBox';
import {createWorksheetNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import WorksheetStepFrame from 'notebook/worksheets/steps/WorksheetStepFrame';
import {areAllMaterialOutputsConfirmed} from 'notebook/worksheets/util/materialOutputsConfirmed';
import {useWorksheetDocumentUpdates} from 'notebook/worksheets/util/useWorksheetDocumentUpdates';
import {useWorksheetRealtimeNotifications} from 'notebook/worksheets/util/useWorksheetRealtimeNotifications';
import {getWorksheetControlVariables} from 'notebook/worksheets/util/worksheetControl';
import {getSelectedStepControlFlags} from 'notebook/worksheets/util/worksheetSelectedStepControl';
import {
  DocumentStatus,
  Worksheet,
  WorksheetStepDocumentSelection,
  WorksheetViewStateAction,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext, WorksheetContextValue} from 'notebook/worksheets/WorksheetContext';
import WorksheetPageHeader from 'notebook/worksheets/workspace/WorksheetPageHeader';
import WorksheetSidepanel from 'notebook/worksheets/workspace/WorksheetSidepanel';
import {
  extractStepTasksData,
  extractMethodTasksData,
  getSelectedStepFromStepId,
  getSelectedStepId,
  setSelectedStepId,
} from 'notebook/worksheets/workspace/WorksheetUtils';
import {STEP_SELECTOR_SIDEBAR_WIDTH} from 'procedures/shared-components/step-config/step-nav/StepNavigationDesignConstants';
import {
  usePMEIsAndMethodTasks,
  useProcedureWorksheetContext,
} from 'procedures/worksheets/ProcedureWorksheetContext';
import {areAllMethodTasksCreated, arePreviousPMEITasksCompleted} from 'procedures/worksheets/WorksheetUtils';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';

import {
  WorksheetStepTasksQuery,
  WorksheetStepTasksQueryVariables,
} from './__generated/WorksheetStepTasksQuery';
import {optimisticStepStatusState} from './OptimisticStepStatusState';

export const WORKSHEET_STEP_TASKS_QUERY = gql`
  query WorksheetStepTasksQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    processTasks(ids: $ids) {
      ... on ProcessTaskNode {
        id
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
          }
        }
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            statusLifecycle {
              id
              transitions {
                id
                outStatus {
                  id
                  displayName
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        lastStatusChange {
          ... on ProcessTaskStatusChangeNode {
            id
            creator {
              ...PartySummaryGraphQL
            }
            createdAt
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PartySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface WorksheetDetailsProps {
  worksheet: Worksheet;
  refetch: (isServerUpdate?: boolean) => Promise<void>;
  onSetWorksheet: (id: string) => boolean;
  isExecutionEnabled: boolean;
  dispatch: (action: WorksheetViewStateAction) => void;
  worksheetViewState: WorksheetViewStateType;
}

interface WorksheetDetailsConnectedProps extends WorksheetDetailsProps {
  hasUnsavedStepDocuments: boolean;
}

function WorksheetDetails({
  worksheet,
  refetch,
  hasUnsavedStepDocuments,
  onSetWorksheet,
  isExecutionEnabled,
  dispatch,
  worksheetViewState,
}: WorksheetDetailsConnectedProps): JSX.Element {
  const worksheetStepIds = worksheet.worksheetStepGroups.flatMap((stepGroup) =>
    stepGroup.worksheetSteps.map((step) => step.document.id)
  );
  const pmeisAndTasks = usePMEIsAndMethodTasks();
  const pmeisWithTasks = useMemo(() => pmeisAndTasks.map((pT) => pT.pmei), [pmeisAndTasks]);

  // Listen for worksheet realtime notifications (e.g., steps reopened due to cross-step refs)
  useWorksheetRealtimeNotifications(worksheet.id);

  const [documentUpdatesManager] = useState(() => new DocumentUpdatesManager());
  const [selectedStep, setSelectedStep] = useState<WorksheetStepDocumentSelection>(() => {
    const selectedStepId = getSelectedStepId(worksheet.id, worksheetStepIds);
    return getSelectedStepFromStepId(selectedStepId || undefined, worksheet);
  });

  useWorksheetDocumentUpdates(worksheet.id, selectedStep?.document?.id);

  const tableNamespace = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
    ? createWorksheetNamespace(worksheet.id)
    : undefined;

  const setSelectedStepCallback = useCallback(
    (selectedStep: WorksheetStepDocumentSelection) => {
      const stepId = selectedStep.document?.id;
      setSelectedStep({...selectedStep, stepId});
      setSelectedStepId(worksheet.id, stepId);
    },
    [setSelectedStep, worksheet.id]
  );

  const crossStepFormulaBoxSteps = useMemo(
    () =>
      worksheet.worksheetStepGroups.flatMap((stepGroup) =>
        stepGroup.worksheetSteps.map((step) => ({documentId: step.document.id, name: step.document.name}))
      ),
    [worksheet.worksheetStepGroups]
  );

  const onReturnToStep = useCallback(
    (stepId: string) => {
      worksheet.worksheetStepGroups.forEach((stepGroup, stepGroupIndex) => {
        stepGroup.worksheetSteps.forEach((step, stepIndex) => {
          if (step.document.id === stepId) {
            setSelectedStepCallback({
              stepIndex,
              stepGroupIndex,
              document: {id: stepId, status: DocumentStatus.LOADED},
            });
          }
        });
      });
    },
    [setSelectedStepCallback, worksheet.worksheetStepGroups]
  );

  const stepGroupIndex = selectedStep?.stepGroupIndex;
  const stepIndex = selectedStep?.stepIndex;
  const step =
    stepGroupIndex !== undefined && stepIndex !== undefined
      ? worksheet.worksheetStepGroups[stepGroupIndex].worksheetSteps[stepIndex]
      : undefined;

  const result = useBenchlingQuery<WorksheetStepTasksQuery, WorksheetStepTasksQueryVariables>(
    WORKSHEET_STEP_TASKS_QUERY,
    {
      variables: {
        ids: step?.tasks.map((task) => (isInaccessible(task) ? task.invalidId : task.id)) ?? [],
      },
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );
  const [optimisticStepStatus] = optimisticStepStatusState.useGlobalClientState(step?.document.id ?? '');
  const processTasks = result.type === QueryResultType.SUCCESS ? result.data.processTasks : undefined;
  const stepTaskData = useMemo(
    () =>
      optimisticStepStatus
        ? {
            ...extractStepTasksData(processTasks),
            status: optimisticStepStatus,
          }
        : extractStepTasksData(processTasks),
    [processTasks, optimisticStepStatus]
  );
  const isDelayedMappingEnabled = BenchlingConfig.STUDIES_ENABLE_SPLIT_POOL;
  const worksheetTaskData = useMemo(
    () => extractMethodTasksData(pmeisAndTasks, isDelayedMappingEnabled),
    [pmeisAndTasks, isDelayedMappingEnabled]
  );
  // An archived worksheet is read-only: all mutating actions (starting/completing runs, transitioning steps,
  // and editing step documents) must be disabled in the UI rather than allowed and then rejected server-side.
  const isWorksheetArchived = worksheet.archiveData.archived;
  const enableStepTransitions = !hasUnsavedStepDocuments && isExecutionEnabled && !isWorksheetArchived;

  // This value is passed through to the TransitionRunModal & leveraged to render an error about confirming
  // material outputs. If this disable complete run logic is ever changed, that error logic will need to be
  // updated too.
  const outputsConfirmed = areAllMaterialOutputsConfirmed(pmeisWithTasks);
  const disableTransitionRunModalCompleteRunButton = !outputsConfirmed;
  const previousPMEITasksComplete = useMemo(
    () => arePreviousPMEITasksCompleted(pmeisWithTasks),
    [pmeisWithTasks]
  );
  const {
    isWorksheetPending,
    worksheetLastActionText,
    isWorksheetStatusBadgeDisabled,
    showWorksheetStatusDropdownButton,
    worksheetStatusBadgeTooltipText,
  } = getWorksheetControlVariables(worksheetTaskData, {
    hasUnsavedStepDocuments,
    areAllMethodTasksCreated: areAllMethodTasksCreated(pmeisWithTasks, worksheet.tasks),
    previousPMEITasksCompleted: previousPMEITasksComplete,
    isDelayedMappingEnabled,
  });
  const isWorksheetInReview = ReviewConstants.LOCKED_STATUSES.includes(String(worksheet.review?.status));
  const isWorksheetStartRequired = isWorksheetPending && !isWorksheetStatusBadgeDisabled;
  const {isSelectedStepActionsLocked, isSelectedStepDocumentLocked} = getSelectedStepControlFlags(
    isWorksheetPending,
    stepGroupIndex,
    stepTaskData.status,
    worksheet.worksheetStepGroups.length,
    isWorksheetInReview || isWorksheetArchived
  );

  // When a new step is added, we do not have the document ID for it as the document will be created on the
  // server. As such, we cannot set the document ID until the refetch is complete. This is why we set the
  // document ID to undefined when adding a new step and then set it to the correct value once the refetch
  // is complete.
  if (step?.document.id !== selectedStep.document.id && step?.document.id) {
    setSelectedStepCallback({
      ...selectedStep,
      document: {id: step.document.id, status: DocumentStatus.LOADED},
    });
  }

  const refetchStepTaskDataRef = useRef(result.refetch);
  useEffect(() => {
    refetchStepTaskDataRef.current = result.refetch;
  }, [result.refetch]);
  const refetchStepTaskData = useCallback(async () => {
    await refetchStepTaskDataRef.current();
  }, []);

  const worksheetContextValue: WorksheetContextValue = useMemo(
    () => ({
      deprecatedStepCreationContextId: step?.creationContextId || null,
      worksheetTaskData,
      worksheet,
      refetch,
      refetchStepTaskData,
      viewer: false,
      disableTransitionRunModalCompleteRunButton,
      isExecutionEnabled,
      dispatch,
      worksheetViewState,
    }),
    [
      step,
      worksheetTaskData,
      worksheet,
      refetch,
      refetchStepTaskData,
      disableTransitionRunModalCompleteRunButton,
      isExecutionEnabled,
      dispatch,
      worksheetViewState,
    ]
  );

  const parentEditorProperties = EditorPropertiesContext.useContext();
  const editorPropertiesContextValue: EditorPropertiesContextInterface = useMemo(
    () => ({
      ...parentEditorProperties,
      deprecatedStepCreationContextId: step?.creationContextId ?? undefined,
    }),
    [parentEditorProperties, step?.creationContextId]
  );

  const {PMEIs: allPMEIs} = useProcedureWorksheetContext();
  const structuredTableInsertionContextValue: StructuredTableInsertionContextValue = useMemo(() => {
    const firstAccessiblePMEI = allPMEIs.find((pmei) => !isInaccessible(pmei));
    if (firstAccessiblePMEI == null || isInaccessible(firstAccessiblePMEI)) {
      return {
        itemInsertedFromId: worksheet.id,
        isProcedureMultibatchExecution: false,
      };
    }
    const firstProcedureRun = firstAccessiblePMEI.procedureRun;
    assert(
      allPMEIs.every((pmei) => isInaccessible(pmei) || pmei.procedureRun.id === firstProcedureRun.id),
      'Expected all PMEIs in the worksheet to have the same parent procedure run'
    );
    return {
      itemInsertedFromId: worksheet.id,
      isProcedureMultibatchExecution:
        firstProcedureRun.defaultDataEntryMode ===
        ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER,
    };
  }, [allPMEIs, worksheet.id]);

  useEffect(() => {
    if (selectedStep.document.id) {
      documentUpdatesManager.changeActiveDocument(selectedStep.document.id);
    }
    return () => {
      documentUpdatesManager.markInactive();
    };
  }, [selectedStep.document.id, documentUpdatesManager]);

  return (
    <RealtimeUpdateSubscriptionWrapper
      apiId={worksheet.id}
      refetch={refetch}
      messageFilter="no_filter"
      /* If CSDR is enabled, updating any step may cause other steps to be reopened so we need to refetch */
      refetchOnMessagesFromSameClient={BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS}
    >
      <WorksheetContext.Provider value={worksheetContextValue}>
        <EditorPropertiesContext.Provider value={editorPropertiesContextValue}>
          <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
            <TabLayout
              backgroundColor={TabLayoutBackgroundColor.WHITE}
              header={
                <WorksheetPageHeader
                  name={worksheet.name}
                  worksheetId={worksheet.id}
                  isWorksheetInReview={isWorksheetInReview}
                  onSetWorksheet={onSetWorksheet}
                  onSetSelectedStep={setSelectedStepCallback}
                  selectedStep={selectedStep}
                  worksheetLastActionText={worksheetLastActionText}
                  isWorksheetStatusBadgeDisabled={
                    isWorksheetStatusBadgeDisabled || !isExecutionEnabled || isWorksheetArchived
                  }
                  showWorksheetStatusDropdownButton={showWorksheetStatusDropdownButton}
                  worksheetStatusBadgeTooltipText={worksheetStatusBadgeTooltipText}
                />
              }
            >
              <Flex direction="row" h="100%">
                <WorksheetSidepanel
                  selectedStep={selectedStep}
                  onSetSelectedStep={setSelectedStepCallback}
                  isWorksheetPending={isWorksheetPending}
                  isWorksheetInReview={isWorksheetInReview}
                />
                {selectedStep.document.status === DocumentStatus.LOADED ? (
                  <Flex direction="column" h="100%" w={`calc(100% - ${STEP_SELECTOR_SIDEBAR_WIDTH}px)`}>
                    <WorksheetStepFrame
                      stepName={step?.document.name}
                      document={selectedStep.document}
                      stepTaskData={stepTaskData}
                      tableNamespace={tableNamespace}
                      isWorksheetStartRequired={isWorksheetStartRequired}
                      isSelectedStepActionsLocked={isSelectedStepActionsLocked || !isExecutionEnabled}
                      isSelectedStepDocumentLocked={isSelectedStepDocumentLocked || !isExecutionEnabled}
                      enableStepTransitions={enableStepTransitions}
                      isExecutionEnabled={isExecutionEnabled}
                      worksheetReviewStatus={worksheet.review?.status}
                      isWorksheetArchived={isWorksheetArchived}
                    />
                    {BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS && tableNamespace && (
                      <ActiveCrossStepFormulaBox
                        tableNamespace={tableNamespace}
                        stepIdBeingDisplayed={selectedStep.document.id}
                        steps={crossStepFormulaBoxSteps}
                        onReturnToStep={onReturnToStep}
                      />
                    )}
                  </Flex>
                ) : selectedStep.document.status === DocumentStatus.LOADING ? (
                  <CenteredHorizontalThrobber />
                ) : undefined}
              </Flex>
            </TabLayout>
          </StructuredTableInsertionContext.Provider>
        </EditorPropertiesContext.Provider>
      </WorksheetContext.Provider>
    </RealtimeUpdateSubscriptionWrapper>
  );
}

function mapDocumentStoreProps(
  documentStore: typeof DocumentStore,
  {worksheet}: WorksheetDetailsProps
): Partial<WorksheetDetailsConnectedProps> {
  const documentIds = worksheet.worksheetStepGroups.flatMap((stepGroup) =>
    stepGroup.worksheetSteps.map((step) => step.document.id)
  );
  const hasUnsavedStepDocuments = documentIds.some(
    (id) => documentStore.inStore(id) && documentStore.hasUnsavedChanges(id)
  );
  return {hasUnsavedStepDocuments};
}

export default compose(DocumentStore.connect(mapDocumentStoreProps))(React.memo(WorksheetDetails));
